import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import {
  BookOpen,
  Plus,
  Search,
  Calendar,
  TrendingUp,
  ShoppingCart,
  CreditCard,
  Settings,
  Clock,
  CheckCircle,
  AlertCircle,
  Eye,
  Edit,
  Trash2,
  Download,
  Upload,
  BarChart3,
  Activity,
  ChevronRight,
  ChevronDown,
  Archive
} from 'lucide-react';

const JournalView = () => {
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [viewMode, setViewMode] = useState('entries'); // entries, batches, summary
  const [filterType, setFilterType] = useState('ALL');
  const [filterStatus, setFilterStatus] = useState('ALL');
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedEntry, setExpandedEntry] = useState(null);
  const [journalEntries, setJournalEntries] = useState([]);
  const [journalEntryLines, setJournalEntryLines] = useState([]);
  const [journalBatches, setJournalBatches] = useState([]);
  const [journalEntryTypes, setJournalEntryTypes] = useState([]);
  const [journalSummary, setJournalSummary] = useState({});
  const [journalUsers, setJournalUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch data from Supabase
  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch journal entries
      const { data: entriesData, error: entriesError } = await supabase
        .from('financial_journal_entries')
        .select('*')
        .order('created_at', { ascending: false });

      if (entriesError) throw entriesError;

      // Fetch journal entry lines
      const { data: linesData, error: linesError } = await supabase
        .from('financial_journal_lines')
        .select('*');

      if (linesError) throw linesError;

      // Fetch employees for journal users
      const { data: employeesData, error: employeesError } = await supabase
        .from('employees')
        .select('*');

      if (employeesError) throw employeesError;

      // Fetch journal batches
      const { data: batchesData, error: batchesError } = await supabase
        .from('financial_journal_batches')
        .select('*')
        .order('batch_date', { ascending: false });

      if (batchesError) throw batchesError;

      setJournalEntries(entriesData || []);
      setJournalEntryLines(linesData || []);
      setJournalUsers(employeesData || []);
      setJournalBatches(batchesData || []);

      // Define journal entry types
      setJournalEntryTypes([
        {
          type_id: 'SALES',
          type_name: '銷售交易',
          icon: 'TrendingUp',
          color_code: '#10B981'
        },
        {
          type_id: 'PURCHASE',
          type_name: '採購交易',
          icon: 'ShoppingCart',
          color_code: '#3B82F6'
        },
        {
          type_id: 'PAYMENT',
          type_name: '付款交易',
          icon: 'CreditCard',
          color_code: '#8B5CF6'
        },
        {
          type_id: 'ADJUSTMENT',
          type_name: '調整分錄',
          icon: 'Settings',
          color_code: '#F59E0B'
        },
        {
          type_id: 'DEPRECIATION',
          type_name: '折舊分錄',
          icon: 'Calendar',
          color_code: '#EF4444'
        },
        {
          type_id: 'ACCRUAL',
          type_name: '應計分錄',
          icon: 'Clock',
          color_code: '#06B6D4'
        }
      ]);

      // Calculate journal summary
      const summary = {
        total_entries: entriesData?.length || 0,
        total_posted: entriesData?.filter(entry => entry.status === 'posted').length || 0,
        total_draft: entriesData?.filter(entry => entry.status === 'draft').length || 0,
        total_review: entriesData?.filter(entry => entry.status === 'review').length || 0,
        total_amount_posted: entriesData?.filter(entry => entry.status === 'posted')
          .reduce((sum, entry) => sum + (parseFloat(entry.total_debit) || 0), 0),
        total_amount_pending: entriesData?.filter(entry => entry.status !== 'posted')
          .reduce((sum, entry) => sum + (parseFloat(entry.total_debit) || 0), 0),
        balance_verified: true,
        last_updated: new Date().toISOString(),
        // Calculate entries by type (normalize to uppercase for display)
        entries_by_type: entriesData?.reduce((acc, entry) => {
          const normalizedType = entry.entry_type?.toUpperCase() || 'OTHER';
          acc[normalizedType] = (acc[normalizedType] || 0) + 1;
          return acc;
        }, {}) || {},
        // Calculate entries by status (normalize to uppercase for display)
        entries_by_status: entriesData?.reduce((acc, entry) => {
          const normalizedStatus = entry.status?.toUpperCase() || 'DRAFT';
          acc[normalizedStatus] = (acc[normalizedStatus] || 0) + 1;
          return acc;
        }, {}) || {}
      };

      setJournalSummary(summary);

    } catch (error) {
      console.error('Error fetching journal data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter entries based on current filters
  const filteredEntries = journalEntries.filter(entry => {
    const matchesType = filterType === 'ALL' || entry.entry_type?.toUpperCase() === filterType;
    const matchesStatus = filterStatus === 'ALL' || entry.status?.toUpperCase() === filterStatus;
    const matchesSearch = !searchTerm || 
      entry.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      entry.entry_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      entry.reference_number?.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesType && matchesStatus && matchesSearch;
  });

  // Get icon for entry type
  const getTypeIcon = (type) => {
    const normalizedType = type?.toUpperCase() || 'OTHER';
    const typeConfig = journalEntryTypes.find(t => t.type_id === normalizedType);
    switch (typeConfig?.icon) {
      case 'TrendingUp': return TrendingUp;
      case 'ShoppingCart': return ShoppingCart;
      case 'CreditCard': return CreditCard;
      case 'Settings': return Settings;
      case 'Calendar': return Calendar;
      case 'Clock': return Clock;
      default: return BookOpen;
    }
  };

  // Get color for entry type
  const getTypeColor = (type) => {
    const normalizedType = type?.toUpperCase() || 'OTHER';
    const typeConfig = journalEntryTypes.find(t => t.type_id === normalizedType);
    return typeConfig?.color_code || '#6B7280';
  };

  // Get status badge
  const getStatusBadge = (status) => {
    const statusConfig = {
      posted: { label: '已過帳', color: 'bg-green-100 text-green-800', icon: CheckCircle },
      draft: { label: '草稿', color: 'bg-gray-100 text-gray-800', icon: Edit },
      review: { label: '審核中', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
      rejected: { label: '已拒絕', color: 'bg-red-100 text-red-800', icon: AlertCircle }
    };
    
    const normalizedStatus = status?.toLowerCase() || 'draft';
    const config = statusConfig[normalizedStatus] || statusConfig.draft;
    const Icon = config.icon;
    
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="w-3 h-3 mr-1" />
        {config.label}
      </span>
    );
  };

  // Get entry lines for a specific entry
  const getEntryLines = (entryId) => {
    return journalEntryLines.filter(line => line.journal_entry_id === entryId);
  };

  // Format currency
  const formatCurrency = (amount) => {
    if (amount === undefined || amount === null) return '-';
    return `HK$${parseFloat(amount).toLocaleString('en-HK', { minimumFractionDigits: 2 })}`;
  };

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('zh-TW', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  // Summary Dashboard
  const SummaryDashboard = () => (
    <div className="space-y-6">
      {/* Hero Section with Unsplash Background */}
      <div className="relative overflow-hidden rounded-lg shadow-lg">
        <img 
          src="https://images.unsplash.com/photo-1554224154-26032ffc0d07?w=1200&h=300&fit=crop&crop=center"
          alt="Journal Management"
          className="w-full h-64 object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/70 to-transparent">
          <div className="h-full flex items-center px-8">
            <div className="text-white max-w-2xl">
              <h1 className="text-4xl font-bold mb-2">日記帳管理系統</h1>
              <h2 className="text-xl text-gray-300 mb-4">Journal Entry Management System</h2>
              <p className="text-lg mb-6">完整的會計分錄記錄與管理平台，確保財務交易的準確性與合規性</p>
              <div className="flex items-center space-x-4 text-sm">
                <span className="bg-white bg-opacity-20 px-3 py-1 rounded-full">
                  📊 本期分錄: {journalSummary.total_entries || 0}筆
                </span>
                <span className="bg-white bg-opacity-20 px-3 py-1 rounded-full">
                  ✅ 已過帳: {journalSummary.total_posted || 0}筆
                </span>
                <span className="bg-white bg-opacity-20 px-3 py-1 rounded-full">
                  💰 過帳金額: {formatCurrency(journalSummary.total_amount_posted || 0)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-l-green-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">已過帳分錄</p>
              <p className="text-2xl font-bold text-gray-800">{journalSummary.total_posted || 0}</p>
            </div>
            <CheckCircle className="w-8 h-8 text-green-500" />
          </div>
          <div className="mt-2">
            <span className="text-xs text-green-600">過帳金額: {formatCurrency(journalSummary.total_amount_posted || 0)}</span>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-l-yellow-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">待處理分錄</p>
              <p className="text-2xl font-bold text-gray-800">{(journalSummary.total_draft || 0) + (journalSummary.total_review || 0)}</p>
            </div>
            <Clock className="w-8 h-8 text-yellow-500" />
          </div>
          <div className="mt-2">
            <span className="text-xs text-yellow-600">待過帳金額: {formatCurrency(journalSummary.total_amount_pending || 0)}</span>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-l-blue-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">本期總分錄</p>
              <p className="text-2xl font-bold text-gray-800">{journalSummary.total_entries || 0}</p>
            </div>
            <BookOpen className="w-8 h-8 text-blue-500" />
          </div>
          <div className="mt-2">
            <span className="text-xs text-blue-600">最後更新: {formatDate(journalSummary.last_updated || new Date())}</span>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-l-purple-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">帳務平衡</p>
              <p className="text-2xl font-bold text-gray-800">{journalSummary.balance_verified ? '✓' : '✗'}</p>
            </div>
            <Activity className="w-8 h-8 text-purple-500" />
          </div>
          <div className="mt-2">
            <span className={`text-xs ${journalSummary.balance_verified ? 'text-green-600' : 'text-red-600'}`}>
              {journalSummary.balance_verified ? '借貸平衡' : '不平衡'}
            </span>
          </div>
        </div>
      </div>

      {/* Entry Types Distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white p-6 rounded-lg shadow-md">
          <h3 className="text-lg font-semibold mb-4">分錄類型分佈</h3>
          <div className="space-y-3">
            {Object.entries(journalSummary.entries_by_type || {}).map(([type, count]) => {
              const typeConfig = journalEntryTypes.find(t => t.type_id === type);
              const percentage = journalSummary.total_entries > 0 ? (count / journalSummary.total_entries * 100).toFixed(1) : '0.0';
              
              return (
                <div key={type} className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div 
                      className="w-4 h-4 rounded"
                      style={{ backgroundColor: typeConfig?.color_code }}
                    ></div>
                    <span className="text-sm font-medium">{typeConfig?.type_name}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-600">{count}筆</span>
                    <span className="text-xs text-gray-500">({percentage}%)</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-md">
          <h3 className="text-lg font-semibold mb-4">分錄狀態分佈</h3>
          <div className="space-y-3">
            {Object.entries(journalSummary.entries_by_status || {}).map(([status, count]) => {
              const percentage = journalSummary.total_entries > 0 ? (count / journalSummary.total_entries * 100).toFixed(1) : '0.0';
              const statusConfig = {
                POSTED: { label: '已過帳', color: '#10B981' },
                DRAFT: { label: '草稿', color: '#6B7280' },
                REVIEW: { label: '審核中', color: '#F59E0B' },
                REJECTED: { label: '已拒絕', color: '#EF4444' }
              };
              
              const config = statusConfig[status] || { label: status, color: '#6B7280' };
              
              return (
                <div key={status} className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div 
                      className="w-4 h-4 rounded"
                      style={{ backgroundColor: config?.color }}
                    ></div>
                    <span className="text-sm font-medium">{config?.label}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-600">{count}筆</span>
                    <span className="text-xs text-gray-500">({percentage}%)</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );

  // Entry Details Modal
  const EntryDetailsModal = ({ entry, onClose }) => {
    const lines = getEntryLines(entry.entry_id);
    const typeConfig = journalEntryTypes.find(t => t.type_id === entry.entry_type);
    const creator = journalUsers.find(u => u.user_id === entry.created_by);
    const approver = journalUsers.find(u => u.user_id === entry.approved_by);

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6 border-b">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-gray-800">分錄詳細資訊</h2>
                <p className="text-gray-600">{entry.entry_number} - {entry.description}</p>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          <div className="p-6 space-y-6">
            {/* Entry Header Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-700">分錄編號</label>
                  <p className="text-lg font-semibold text-gray-900">{entry.entry_number}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">分錄日期</label>
                  <p className="text-gray-900">{formatDate(entry.entry_date)}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">分錄類型</label>
                  <div className="flex items-center space-x-2">
                    <div 
                      className="w-4 h-4 rounded"
                      style={{ backgroundColor: typeConfig?.color_code }}
                    ></div>
                    <span className="text-gray-900">{typeConfig?.type_name}</span>
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">參考類型</label>
                  <p className="text-gray-900">{entry.reference_type}</p>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-700">參考編號</label>
                  <p className="text-gray-900">{entry.reference_number}</p>
                </div>
                                 <div>
                   <label className="text-sm font-medium text-gray-700">總金額</label>
                   <p className="text-xl font-bold text-gray-900">{formatCurrency(entry.total_debit)}</p>
                 </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">狀態</label>
                  <div>{getStatusBadge(entry.status)}</div>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">建立者</label>
                  <p className="text-gray-900">{creator?.user_name || entry.created_by}</p>
                </div>
              </div>
            </div>

            {/* Entry Lines */}
            <div>
              <h3 className="text-lg font-semibold mb-4">分錄明細</h3>
              <div className="overflow-x-auto">
                <table className="w-full border border-gray-200 rounded-lg">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        科目代碼
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        科目名稱
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        說明
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        借方金額
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        貸方金額
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {lines.map((line) => (
                      <tr key={line.line_id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">
                          {line.account_code}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {line.account_name}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {line.description}
                        </td>
                        <td className="px-4 py-3 text-sm text-right text-gray-900">
                          {line.debit_amount > 0 ? formatCurrency(line.debit_amount) : '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-right text-gray-900">
                          {line.credit_amount > 0 ? formatCurrency(line.credit_amount) : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-gray-50">
                    <tr>
                      <td colSpan="3" className="px-4 py-3 text-sm font-medium text-gray-900">
                        合計
                      </td>
                      <td className="px-4 py-3 text-sm text-right font-bold text-gray-900">
                        {formatCurrency(lines.reduce((sum, line) => sum + line.debit_amount, 0))}
                      </td>
                      <td className="px-4 py-3 text-sm text-right font-bold text-gray-900">
                        {formatCurrency(lines.reduce((sum, line) => sum + line.credit_amount, 0))}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>

            {/* Notes and Audit Trail */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">備註</h4>
                <p className="text-gray-900 bg-gray-50 p-3 rounded-lg">{entry.notes || '無備註'}</p>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">審核記錄</h4>
                <div className="space-y-2 bg-gray-50 p-3 rounded-lg">
                  <div className="flex justify-between text-sm">
                    <span>建立時間:</span>
                    <span>{formatDate(entry.created_at)}</span>
                  </div>
                  {entry.approved_at && (
                    <div className="flex justify-between text-sm">
                      <span>核准時間:</span>
                      <span>{formatDate(entry.approved_at)}</span>
                    </div>
                  )}
                  {entry.posted_at && (
                    <div className="flex justify-between text-sm">
                      <span>過帳時間:</span>
                      <span>{formatDate(entry.posted_at)}</span>
                    </div>
                  )}
                  {approver && (
                    <div className="flex justify-between text-sm">
                      <span>核准者:</span>
                      <span>{approver.user_name}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Entries List View
  const EntriesListView = () => (
    <div className="space-y-6">
      {/* Filters and Search */}
      <div className="bg-white p-6 rounded-lg shadow-md">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center space-x-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="搜尋分錄編號、說明或參考編號..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent w-64"
              />
            </div>
            
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="ALL">所有類型</option>
              {journalEntryTypes.map(type => (
                <option key={type.type_id} value={type.type_id}>{type.type_name}</option>
              ))}
            </select>

            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="ALL">所有狀態</option>
              <option value="POSTED">已過帳</option>
              <option value="DRAFT">草稿</option>
              <option value="REVIEW">審核中</option>
            </select>
          </div>

          <div className="flex items-center space-x-2">
            <button className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
              <Plus className="w-4 h-4" />
              <span>新增分錄</span>
            </button>
            <button className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
              <Upload className="w-4 h-4" />
              <span>匯入</span>
            </button>
            <button className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
              <Download className="w-4 h-4" />
              <span>匯出</span>
            </button>
          </div>
        </div>
      </div>

      {/* Entries Table */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  分錄編號
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  日期
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  類型
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  說明
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  金額
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  狀態
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  操作
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredEntries.map((entry) => {
                const TypeIcon = getTypeIcon(entry.entry_type);
                const typeConfig = journalEntryTypes.find(t => t.type_id === entry.entry_type);
                
                return (
                  <React.Fragment key={entry.entry_id}>
                    <tr className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <button
                            onClick={() => setExpandedEntry(expandedEntry === entry.entry_id ? null : entry.entry_id)}
                            className="mr-2 text-gray-400 hover:text-gray-600"
                          >
                            {expandedEntry === entry.entry_id ? 
                              <ChevronDown className="w-4 h-4" /> : 
                              <ChevronRight className="w-4 h-4" />
                            }
                          </button>
                          <div>
                            <div className="text-sm font-medium text-gray-900">{entry.entry_number}</div>
                            <div className="text-sm text-gray-500">{entry.reference_number}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatDate(entry.entry_date)}
                      </td>
                                             <td className="px-6 py-4 whitespace-nowrap">
                         <div className="flex items-center">
                           <TypeIcon 
                             className="w-4 h-4 mr-2" 
                             style={{ color: getTypeColor(entry.entry_type) }}
                           />
                           <span className="text-sm text-gray-900">
                             {entry.entry_type === 'sales' ? '銷售交易' :
                              entry.entry_type === 'purchase' ? '採購交易' :
                              entry.entry_type === 'payment' ? '付款交易' :
                              entry.entry_type === 'adjustment' ? '調整分錄' :
                              entry.entry_type === 'depreciation' ? '折舊分錄' :
                              entry.entry_type === 'accrual' ? '應計分錄' :
                              entry.entry_type || '其他'}
                           </span>
                         </div>
                       </td>
                      <td className="px-6 py-4 text-sm text-gray-900 max-w-xs truncate">
                        {entry.description}
                      </td>
                                             <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900 font-medium">
                         {formatCurrency(entry.total_debit)}
                       </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(entry.status)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => setSelectedEntry(entry)}
                            className="text-blue-600 hover:text-blue-900"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button className="text-gray-600 hover:text-gray-900">
                            <Edit className="w-4 h-4" />
                          </button>
                          {entry.status === 'DRAFT' && (
                            <button className="text-red-600 hover:text-red-900">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                    
                                         {/* Expanded Entry Details */}
                     {expandedEntry === entry.entry_id && (
                       <tr>
                         <td colSpan="7" className="px-6 py-4 bg-gray-50">
                           <div className="space-y-4">
                             <h4 className="text-sm font-medium text-gray-700 mb-3">分錄明細</h4>
                             <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                               {getEntryLines(entry.entry_id).map((line) => (
                                 <div key={line.line_id} className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
                                   <div className="flex justify-between items-start">
                                     <div className="flex-1">
                                       <div className="flex items-center space-x-2 mb-2">
                                         <span className="text-sm font-semibold text-gray-900">
                                           {line.account_code}
                                         </span>
                                         <span className="text-sm text-gray-600">-</span>
                                         <span className="text-sm font-medium text-gray-800">
                                           {line.account_name}
                                         </span>
                                       </div>
                                       <p className="text-xs text-gray-600 mb-2">{line.description}</p>
                                     </div>
                                     <div className="text-right ml-4">
                                       {parseFloat(line.debit_amount) > 0 && (
                                         <div className="text-right">
                                           <p className="text-sm font-bold text-green-600">
                                             借: {formatCurrency(line.debit_amount)}
                                           </p>
                                         </div>
                                       )}
                                       {parseFloat(line.credit_amount) > 0 && (
                                         <div className="text-right">
                                           <p className="text-sm font-bold text-red-600">
                                             貸: {formatCurrency(line.credit_amount)}
                                           </p>
                                         </div>
                                       )}
                                     </div>
                                   </div>
                                 </div>
                               ))}
                             </div>
                             
                             {/* Summary totals */}
                             <div className="mt-4 pt-4 border-t border-gray-200">
                               <div className="flex justify-between items-center text-sm">
                                 <span className="font-medium text-gray-700">合計:</span>
                                 <div className="flex space-x-6">
                                   <span className="text-green-600 font-bold">
                                     借: {formatCurrency(getEntryLines(entry.entry_id).reduce((sum, line) => sum + parseFloat(line.debit_amount || 0), 0))}
                                   </span>
                                   <span className="text-red-600 font-bold">
                                     貸: {formatCurrency(getEntryLines(entry.entry_id).reduce((sum, line) => sum + parseFloat(line.credit_amount || 0), 0))}
                                   </span>
                                 </div>
                               </div>
                             </div>
                           </div>
                         </td>
                       </tr>
                     )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  // Get batch type icon and color
  const getBatchTypeIcon = (type) => {
    switch (type) {
      case 'sales_collection': return TrendingUp;
      case 'purchase': return ShoppingCart;
      case 'payment': return CreditCard;
      case 'adjustment': return Settings;
      case 'retail_sales': return TrendingUp;
      case 'depreciation': return Calendar;
      case 'payroll': return Clock;
      default: return BookOpen;
    }
  };

  const getBatchTypeColor = (type) => {
    switch (type) {
      case 'sales_collection': return '#10B981';
      case 'purchase': return '#3B82F6';
      case 'payment': return '#8B5CF6';
      case 'adjustment': return '#F59E0B';
      case 'retail_sales': return '#10B981';
      case 'depreciation': return '#EF4444';
      case 'payroll': return '#06B6D4';
      default: return '#6B7280';
    }
  };

  // Get status badge for batches
  const getBatchStatusBadge = (status) => {
    const statusConfig = {
      posted: { label: '已過帳', color: 'bg-green-100 text-green-800' },
      draft: { label: '草稿', color: 'bg-gray-100 text-gray-800' },
      review: { label: '審核中', color: 'bg-yellow-100 text-yellow-800' },
      rejected: { label: '已拒絕', color: 'bg-red-100 text-red-800' }
    };
    
    const normalizedStatus = status?.toLowerCase() || 'draft';
    const config = statusConfig[normalizedStatus] || statusConfig.draft;
    
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${config.color}`}>
        {config.label}
      </span>
    );
  };

  // Batches View
  const BatchesView = () => (
    <div className="space-y-6">
      {/* Batches Header */}
      <div className="bg-white p-6 rounded-lg shadow-md">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-800">分錄批次管理</h2>
            <p className="text-gray-600">管理和追蹤分錄批次處理狀況</p>
          </div>
          <button className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
            <Plus className="w-4 h-4" />
            <span>新增批次</span>
          </button>
        </div>
      </div>

      {/* Batches Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {journalBatches.map((batch) => {
          const BatchTypeIcon = getBatchTypeIcon(batch.batch_type);
          
          return (
            <div key={batch.batch_id} className="bg-white p-6 rounded-lg shadow-md border-l-4 border-l-blue-500 hover:shadow-lg transition-shadow duration-200">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <BatchTypeIcon 
                    className="w-5 h-5" 
                    style={{ color: getBatchTypeColor(batch.batch_type) }}
                  />
                  <h3 className="text-lg font-semibold text-gray-800">{batch.batch_name}</h3>
                </div>
                {getBatchStatusBadge(batch.status)}
              </div>
              
              <div className="space-y-3 mb-4">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">批次編號:</span>
                  <span className="font-medium text-gray-900">{batch.batch_number}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">批次日期:</span>
                  <span className="text-gray-900">{formatDate(batch.batch_date)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">分錄數量:</span>
                  <span className="font-medium text-gray-900">{batch.total_entries}筆</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">總金額:</span>
                  <span className="font-medium text-gray-900">{formatCurrency(batch.total_amount)}</span>
                </div>
              </div>
              
              <p className="text-sm text-gray-600 mb-4 leading-relaxed">{batch.description}</p>
              
              <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                <div className="text-xs text-gray-500">
                  建立者: <span className="font-medium">{batch.created_by}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <button className="text-blue-600 hover:text-blue-900 transition-colors">
                    <Eye className="w-4 h-4" />
                  </button>
                  <button className="text-gray-600 hover:text-gray-900 transition-colors">
                    <Edit className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Navigation Tabs */}
      <div className="bg-white p-1 rounded-lg shadow-sm">
        <div className="flex space-x-1">
          <button
            onClick={() => setViewMode('summary')}
            className={`flex items-center space-x-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              viewMode === 'summary'
                ? 'bg-blue-600 text-white'
                : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
            }`}
          >
            <BarChart3 className="w-4 h-4" />
            <span>總覽</span>
          </button>
          <button
            onClick={() => setViewMode('entries')}
            className={`flex items-center space-x-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              viewMode === 'entries'
                ? 'bg-blue-600 text-white'
                : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
            }`}
          >
            <BookOpen className="w-4 h-4" />
            <span>分錄管理</span>
          </button>
          <button
            onClick={() => setViewMode('batches')}
            className={`flex items-center space-x-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              viewMode === 'batches'
                ? 'bg-blue-600 text-white'
                : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
            }`}
          >
            <Archive className="w-4 h-4" />
            <span>批次管理</span>
          </button>
        </div>
      </div>

      {/* Content */}
      {viewMode === 'summary' && <SummaryDashboard />}
      {viewMode === 'entries' && <EntriesListView />}
      {viewMode === 'batches' && <BatchesView />}

      {/* Entry Details Modal */}
      {selectedEntry && (
        <EntryDetailsModal
          entry={selectedEntry}
          onClose={() => setSelectedEntry(null)}
        />
      )}
    </div>
  );
};

export default JournalView; 